/**
 * Stock presence in Product Detail (PHASE1-CREATED-PRODUCT-READINESS-001).
 *
 * A product that was just created reliably HAS a row, but its inventory
 * balances only exist once stock was actually posted. The detail page summed an
 * empty balance set to `0` and rendered "0 available", which reads as a measured
 * zero. A missing balance row and a measured zero are different facts:
 *
 *   - no row  → nothing has been posted yet. Opening stock may still be on its
 *               way, or may never have been requested. Not a stock count.
 *   - a row   → a real count exists (which may legitimately be zero).
 *
 * This distinction is presentation only. It never derives stock and never
 * substitutes a value: the numbers still come from `inventory_balances`.
 */

export type StockPresenceState = "recorded" | "not_recorded";

export interface StockPresence {
  state: StockPresenceState;
  /** Short heading for the detail summary. */
  label: string;
  /** One-line explanation. Never claims stock is zero or that it exists. */
  detail: string;
}

export function stockPresence(params: {
  /** Null/undefined = the balance rows were not read, so nothing may be claimed. */
  balanceRowCount: number | null | undefined;
}): StockPresence {
  // Unknown evidence is not evidence of absence: keep the existing numeric
  // presentation rather than inventing a "nothing recorded" state.
  if (params.balanceRowCount == null) {
    return {
      state: "recorded",
      label: "Available",
      detail: "Counts come from the inventory ledger.",
    };
  }
  const balances = Number.isFinite(params.balanceRowCount) ? Math.max(0, Math.trunc(params.balanceRowCount)) : 0;
  if (balances > 0) {
    return {
      state: "recorded",
      label: "Available",
      detail: "Counts come from the inventory ledger.",
    };
  }
  return {
    state: "not_recorded",
    label: "Not recorded",
    detail: "No inventory balance yet — not the same as zero on hand.",
  };
}

/** Whether the detail page may present a stock total as a count. */
export function stockIsRecorded(presence: StockPresence): boolean {
  return presence.state === "recorded";
}